<script setup lang="ts">
/**
 * Действия над отмеченными строками списка.
 *
 * Чекбоксы в макете есть, а панели над ними не нарисовано (DEV-1501): отметка
 * умела только подсвечивать строку. Панель собрана из тех же выпадашек, что
 * стоят в самой строке, — статус и исполнитель, ровно то, что меняют пачкой.
 */
import { ChevronDown } from 'lucide-vue-next'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import InlinePicker from '@/components/InlinePicker.vue'

import type { ProjectColumn, ProjectMember } from '@/features/projects/api'

const props = defineProps<{
  count: number
  columns: ProjectColumn[]
  members: ProjectMember[]
  busy?: boolean
}>()

const emit = defineEmits<{ move: [string]; assign: [string, string]; clear: [] }>()

const { t } = useI18n()

const statusOptions = computed(() =>
  props.columns.map((column) => ({ value: column.id, label: column.name })),
)

const assigneeOptions = computed(() => [
  { value: '', label: t('issues.list.noAssignee') },
  ...props.members
    .filter((member) => member.name)
    .map((member) => ({ value: member.id, label: member.name })),
])

const onAssign = (assigneeId: string) => {
  emit('assign', assigneeId, props.members.find((member) => member.id === assigneeId)?.name ?? '')
}
</script>

<template>
  <div class="bulk" role="group" :aria-label="t('issues.list.bulk.label')">
    <span class="bulk__count">{{ t('issues.list.selected', { count }) }}</span>

    <InlinePicker :options="statusOptions" model-value="" @update:model-value="emit('move', String($event))">
      <template #trigger="{ isOpen, toggle }">
        <button type="button" class="bulk__action" :disabled="busy" :aria-expanded="isOpen" @click="toggle()">
          {{ t('issues.list.bulk.status') }}
          <ChevronDown :size="14" />
        </button>
      </template>
    </InlinePicker>

    <InlinePicker :options="assigneeOptions" model-value="" @update:model-value="onAssign(String($event))">
      <template #trigger="{ isOpen, toggle }">
        <button type="button" class="bulk__action" :disabled="busy" :aria-expanded="isOpen" @click="toggle()">
          {{ t('issues.list.bulk.assignee') }}
          <ChevronDown :size="14" />
        </button>
      </template>
    </InlinePicker>

    <span class="bulk__spacer" />

    <button type="button" class="bulk__clear" @click="emit('clear')">
      {{ t('issues.list.clearSelection') }}
    </button>
  </div>
</template>

<style scoped>
.bulk {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 16px;
  border: 1px solid var(--color-primary);
  border-radius: var(--radius-lg);
  background: var(--color-primary-tint);
}

.bulk__count {
  color: var(--color-primary-hover);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
}

.bulk__action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.bulk__action:disabled {
  opacity: 0.6;
  cursor: default;
}

.bulk__spacer {
  flex: 1;
}

.bulk__clear {
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}
</style>
